/**
 * Desktop (`lg` and up) layout of the contact band. Rendered by `../ContactUs.tsx` →
 * `ContactUsSection`, and directly by the pages listed in that file's header: the careers pages,
 * which set `isCareers`, and pages that pick their own props.
 * It wraps itself in `Media greaterThanOrEqual="lg"`, so it renders nothing below `lg`.
 *
 * Related: `../Mobile/ContactUs.tsx` (the same band below `lg`), `../Shared/ContactCard.tsx`.
 */
import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Media } from "@ryerson/frontend.next";
import { ContactUsComponentProps } from "../ContactUs";
import ContactCard from "../Shared/ContactCard";
import { Typography } from "@ryerson/frontend.typography";

// Out of flow and moved up 175px, so the headline sits in the band's top padding instead of
// pushing the photo row down.
const TextWrapper = styled.div`
	position: absolute;
	transform: translate(0px, -175px);
	white-space: pre-line;
`;

const ImageContainer = styled.div`
	flex-grow: 1;
	padding-right: 40px;
	text-align: right;
	img {
		display: block;
		margin-left: auto;
		margin-right: 0px;
	}
`;

const CardContainer = styled(Flex)`
	width: 250px;
`;

const CardItem = styled(FlexItem)`
	width: 260px;
`;

const Wrapper = styled(Flex)`
	margin-top: 40px;
`;

/**
 * Two layouts:
 * - `withImage`: the headline sits above the photo, with the first card (accent colour) stacked
 *   over the second (dark) to its right.
 * - otherwise: the headline on the left and the cards in a row on the right, the second card
 *   first.
 *
 * `oneButtonVariant` drops the first card in both. The accent colour is the theme's
 * `primaryBrand` on the `southernToolSteel` build and `accentBrand` on Ryerson.
 */
const ContactUsDesktop: React.FC<ContactUsComponentProps> = ({
	withImage,
	content,
	background,
	oneButtonVariant,
	isCareers,
}) => {
	const { theme } = useTheme();
	const {
		title,
		titleSpecial,
		button1,
		button2,
		imageUrl,
		button1Link,
		button2Link,
		newButton1,
		newButton2,
	} = content;

	return (
		<Media greaterThanOrEqual="lg">
			{withImage ? (
				<ContentSection vPadding="100px" type={background}>
					<div css={{ height: "100px" }}></div>
					<TextWrapper>
						<Typography
							variant="h1"
							color={theme.colors.primary.secondaryBrand}
							css={{ marginTop: "35px" }}
						>
							{title}&nbsp;
							<Typography font="inherit" color={theme.colors.primary.gray}>
								{titleSpecial}
							</Typography>
						</Typography>
					</TextWrapper>
					<Flex>
						<ImageContainer>
							<img src={imageUrl} alt={title} />
						</ImageContainer>
						<CardContainer
							justifyContent={oneButtonVariant ? "flex-end" : "space-between"}
							direction="column"
						>
							{oneButtonVariant === false ? (
								<FlexItem>
									<ContactCard
										color={
											process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
												? theme.colors.primary.primaryBrand
												: theme.colors.primary.accentBrand
										}
										description={isCareers ? newButton1 : button1}
										textColor={theme.colors.primary.secondaryBrand}
										buttonColor={theme.colors.primary.secondaryBrand}
										buttonLink={
											isCareers
												? `${process.env.NEXT_PUBLIC_CAREERS_URL_USA}`
												: button1Link
										}
										isCareers={isCareers}
									/>
								</FlexItem>
							) : (
								<></>
							)}
							<FlexItem>
								<ContactCard
									color={theme.colors.primary.secondaryBrand}
									description={isCareers ? newButton2 : button2}
									textColor={theme.colors.primary.white}
									buttonColor={
										process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
											? theme.colors.primary.primaryBrand
											: theme.colors.primary.accentBrand
									}
									foreground="dark"
									buttonLink={
										isCareers
											? `${process.env.NEXT_PUBLIC_CAREERS_URL_CANADA}`
											: button2Link
									}
									isCareers={isCareers}
								/>
							</FlexItem>
						</CardContainer>
					</Flex>
				</ContentSection>
			) : (
				<ContentSection vPadding="45px" type={background}>
					<Flex justifyContent="space-between">
						<FlexItem
							css={css`
								white-space: pre-line;
							`}
						>
							<Typography
								variant="h2"
								css={css`
									margin-top: 40px;
								`}
							>
								{title}&nbsp;
								<Typography font="inherit" color={theme.colors.primary.gray}>
									{titleSpecial}
								</Typography>
							</Typography>
						</FlexItem>
						<Wrapper>
							<CardItem css={{ marginRight: "40px" }}>
								<ContactCard
									color={theme.colors.primary.secondaryBrand}
									description={isCareers ? newButton2 : button2}
									textColor={theme.colors.primary.white}
									buttonColor={
										process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
											? theme.colors.primary.primaryBrand
											: theme.colors.primary.accentBrand
									}
									foreground="dark"
									withImage={withImage}
									buttonLink={
										isCareers
											? `${process.env.NEXT_PUBLIC_CAREERS_URL_CANADA}`
											: button2Link
									}
									isCareers={isCareers}
								/>
							</CardItem>
							{oneButtonVariant === false ? (
								<CardItem>
									<ContactCard
										color={
											process.env.NEXT_PUBLIC_COMPANY === "southernToolSteel"
												? theme.colors.primary.primaryBrand
												: theme.colors.primary.accentBrand
										}
										description={isCareers ? newButton1 : button1}
										textColor={theme.colors.primary.secondaryBrand}
										buttonColor={theme.colors.primary.secondaryBrand}
										foreground="light"
										withImage={withImage}
										buttonLink={
											isCareers
												? `${process.env.NEXT_PUBLIC_CAREERS_URL_USA}`
												: button1Link
										}
										isCareers={isCareers}
									/>
								</CardItem>
							) : (
								<></>
							)}
						</Wrapper>
					</Flex>
				</ContentSection>
			)}
		</Media>
	);
};

export default ContactUsDesktop;
